// checklistItemActions.js
// Workflow Task Checklist action helpers (modal field allowlist + labels).

import { isMoneyFieldType, normalizeSchemaFieldType } from 'utilities/FieldTypes';
import {
  buildMediaChecklistAction,
  buildSetFieldChecklistAction,
  buildTaskAttachChecklistAction,
  CHECKLIST_ITEM_ACTION_TYPES,
  CHECKLIST_MEDIA_ACTION_TYPES,
  CHECKLIST_RECORD_ACTION_TYPES,
  CHECKLIST_TASK_ATTACH_ACTION_TYPES,
  findMissingRequiredSetFieldAction,
  getChecklistActionDefaultLabel,
  getChecklistActionIconKey,
  getChecklistMediaActionMenuOptions,
  getChecklistRecordActionMenuOptions,
  getChecklistTaskAttachActionMenuOptions,
  isChecklistHostFieldValuePresent,
  isChecklistMediaActionType,
  isChecklistRecordActionType,
  isChecklistSetFieldActionType,
  isChecklistTaskAttachActionType,
  normalizeChecklistItemActions,
  suggestChecklistAddLinkDefaultTitle,
} from 'utilities/ChecklistItemActions';
import {
  getMissionFieldKey,
  getMissionFieldLabel,
} from 'missions/taskmodal/missionTaskModalUtils';

export {
  buildMediaChecklistAction,
  buildSetFieldChecklistAction,
  buildTaskAttachChecklistAction,
  CHECKLIST_ITEM_ACTION_TYPES,
  CHECKLIST_MEDIA_ACTION_TYPES,
  CHECKLIST_RECORD_ACTION_TYPES,
  CHECKLIST_TASK_ATTACH_ACTION_TYPES,
  findMissingRequiredSetFieldAction,
  getChecklistActionDefaultLabel,
  getChecklistActionIconKey,
  getChecklistMediaActionMenuOptions,
  getChecklistRecordActionMenuOptions,
  getChecklistTaskAttachActionMenuOptions,
  isChecklistHostFieldValuePresent,
  isChecklistMediaActionType,
  isChecklistRecordActionType,
  isChecklistSetFieldActionType,
  isChecklistTaskAttachActionType,
  normalizeChecklistItemActions,
  suggestChecklistAddLinkDefaultTitle,
};

/** Host-record field types that open an existing editor modal. */
export const CHECKLIST_SET_FIELD_MODAL_TYPES = Object.freeze([
  'people',
  'date',
  'datetime',
  'daterange',
  'datetimerange',
  'tags',
  'location',
  'relation',
  'percentage',
  'money',
  'enum',
  'pipeline',
  'multiselect',
]);

const SET_FIELD_MODAL_TYPE_SET = new Set(CHECKLIST_SET_FIELD_MODAL_TYPES);

const normalizeString = (value) => String(value || '').trim();

/**
 * @param {unknown} fieldTypeOrField - Schema type string, or a field object.
 * @returns {string}
 */
export function resolveChecklistActionModalType(fieldTypeOrField) {
  if (fieldTypeOrField && typeof fieldTypeOrField === 'object') {
    if (isMoneyFieldType(fieldTypeOrField)) return 'money';
    const type = normalizeSchemaFieldType(
      fieldTypeOrField.type || fieldTypeOrField.fieldType,
    );
    if (SET_FIELD_MODAL_TYPE_SET.has(type)) return type;
    return '';
  }
  const type = normalizeSchemaFieldType(fieldTypeOrField);
  if (SET_FIELD_MODAL_TYPE_SET.has(type)) return type;
  return '';
}

/**
 * @param {unknown} fields
 * @returns {Array<object>}
 */
export function getChecklistActionModalFields(fields) {
  const list = Array.isArray(fields) ? fields : [];
  const seen = new Set();
  const result = [];

  list.forEach((field) => {
    if (!field || typeof field !== 'object') return;
    // Prefer schema `name` (data path) over optional `key` aliases.
    const key = String(field?.name || field?.key || field?.value || '').trim()
      || getMissionFieldKey(field);
    const modalType = resolveChecklistActionModalType(field);
    if (!key || !modalType || seen.has(key)) return;
    seen.add(key);
    result.push({
      key,
      label: getMissionFieldLabel(field) || key,
      modalType,
      rawField: field,
      type: modalType,
    });
  });

  return result.sort((left, right) => left.label.localeCompare(right.label));
}

/**
 * @param {object|null|undefined} action
 * @param {Array<object>} [modalFields]
 * @returns {string}
 */
export function getChecklistActionButtonLabel(action, modalFields = []) {
  if (!action || typeof action !== 'object') return 'Action';

  const explicit = normalizeString(action.label);
  if (explicit) return explicit;

  if (isChecklistSetFieldActionType(action.type)) {
    const fieldKey = normalizeString(action.fieldKey);
    const match = (Array.isArray(modalFields) ? modalFields : [])
      .find((field) => field.key === fieldKey);
    const fieldLabel = normalizeString(match?.label) || fieldKey || 'Field';
    return `Set ${fieldLabel}`;
  }

  return getChecklistActionDefaultLabel(action);
}

/**
 * Maps a checklist task-attach action to the contribution quick-action key.
 *
 * @param {object|null|undefined} action
 * @returns {string}
 */
export function resolveChecklistMediaContributionKey(action) {
  const type = normalizeString(action?.type);
  if (type === CHECKLIST_ITEM_ACTION_TYPES.TAKE_PHOTO) return 'CAMERA';
  if (type === CHECKLIST_ITEM_ACTION_TYPES.RECORD_VIDEO) return 'RECORDING';
  if (type === CHECKLIST_ITEM_ACTION_TYPES.UPLOAD_FILES) return 'ATTACH';
  if (type === CHECKLIST_ITEM_ACTION_TYPES.ADD_NOTE) return 'NOTE';
  if (type === CHECKLIST_ITEM_ACTION_TYPES.CREATE_SKETCH) return 'SKETCH';
  if (type === CHECKLIST_ITEM_ACTION_TYPES.SCHEDULE_EVENT) return 'EVENT';
  if (type === CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK) return 'LINK';
  return '';
}
